'use client'

import {
  chakra,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
} from '@chakra-ui/react'
import { ComponentProps } from '@/baseComponents'
import { Pagination as ArkPagination } from '@ark-ui/react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { CustomGrid } from '@/components/layout/grid'
import { Pagination } from '@/components/molecules/pagination'
import { SearchBar } from '@/components/molecules/searchBar'
import { useStore } from 'zustand'
import { QueryParametersStoreContext } from '@/stores/queryParametersStore'
import { useUrlPagination } from '@/utils/useUrlPagination'
import { usePathname } from 'next/navigation'
import { useSearchResultsStore } from '@/stores/searchResultsStore'
import { Card, CardProps } from '@/components/molecules/card'
import { ButtonProps } from '@/components/atoms/button'
import { BuildUmbracoUrl } from '@/utils/urlHelpers'
import { useTranslations } from 'next-intl'

export interface SearchBlockOverviewProps extends ComponentProps {
  title: string
  pageSize: number
  contentBlocks: any
  searchQueryProps: (props: SearchQueryProps) => void
  homePageIdentifier?: string
}

export interface SearchQueryProps {
  searchQuery?: string
  pageInfo: ArkPagination.PageChangeDetails | undefined
  setSearchResultsCallback: (result: SearchResults) => void
  homePageIdentifier?: string
}

export interface searchQuery {
  query: string
  setSearchCallback: (result: SearchResults) => void
}
export interface SearchResults {
  results: any
  totalItems: number
  totalPages: number
  currentPage: number
}

/**
 * Represents a search block that allows users to input a search query and view paginated search results.
 * @param title - The title of the search block.
 * @param pageSize - The number of items to display per page.
 * @param searchQueryProps - A function that takes `SearchQueryProps` as an argument.
 * @param searchQuery - The search query string (optional).
 * @param pageInfo - Pagination details from `ArkPagination.PageChangeDetails` (optional).
 * @param setSearchResultsCallback - A callback function to set the search results.
 * @param searchResultsItem - The search results items.
 * @param totalItems - The total number of items found.
 * @param totalPages - The total number of pages.
 * @param currentPage - The current page number.
 *
 * @returns SearchBlockOverview component.
 */
export default chakra(SearchPage)

export function SearchPage({
  theme = 'brand',
  title,
  pageSize,
  contentBlocks,
  searchQueryProps,
  homePageIdentifier,
  ...props
}: SearchBlockOverviewProps) {
  const t = useTranslations()
  const [searchPageInfo, setSearchPageInfo] = useUrlPagination(
    pageSize,
    'search',
  )

  const [isLoading, setIsLoading] = useState(true)
  const pathName = usePathname()

  const searchInputRef = useRef<HTMLInputElement>(null)
  const results = useSearchResultsStore((s) => s.result)
  const updateResults = useSearchResultsStore((s) => s.updateResult)

  const queryParamContext = useContext(QueryParametersStoreContext)
  if (!queryParamContext) {
    throw new Error('Search params store not found in tree')
  }

  const queryParamStore = useStore(queryParamContext)
  const searchQuery = queryParamStore.query

  // Use effect for updating the search param store and updating the url structure.
  useEffect(() => {
    if (typeof window !== undefined) {
      let url = pathName
      if (queryParamStore?.queryString) {
        url += `?${queryParamStore?.queryString?.toString()}`
      }
      window.history.replaceState({}, '', url)
    }
  }, [pathName, queryParamStore.queryString])

  // Use effect logic for handling when component is unmounted.
  useEffect(() => {
    return () => {
      if (!!queryParamStore.queryString) {
        return
      }

      queryParamStore.resetQueryParams()
      updateResults(undefined)
      window.history.replaceState({}, '', null)
    }
    // disable eslint warning for missing dependencies
    // because it needs to run on page render for cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInputRef.current && !searchInputRef.current.value) {
      searchInputRef.current.focus()
    }
  })

  useEffect(() => {
    if (!searchQuery) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)

    // Handle async search operation
    const performSearch = async () => {
      try {
        await searchQueryProps({
          searchQuery: searchQuery,
          pageInfo: searchPageInfo,
          setSearchResultsCallback: (result: SearchResults) => {
            updateResults(result)
            setIsLoading(false)
          },
          homePageIdentifier: homePageIdentifier,
        })
      } catch (error) {
        console.error('Search operation failed:', error)
        setIsLoading(false)
      }
    }

    performSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPageInfo, searchQuery])

  const handleSearch = () => {
    if (!searchInputRef.current) {
      return
    }

    const searchValue = searchInputRef.current.value
    if (!searchValue) {
      return
    }
    // reset pageInfo if searchValue differs from previous searchQuery
    let searchInfo = searchPageInfo
    if (searchValue !== searchQuery) {
      searchInfo = {
        page: 1,
        pageSize: pageSize,
      }
      setSearchPageInfo(searchInfo)
    }

    queryParamStore.updateQueryParams({
      query: searchValue,
      pageInfo: searchInfo,
    })
  }

  const startItem = ((results?.currentPage ?? 1) - 1) * pageSize + 1
  const endItem = Math.min(
    (results?.currentPage ?? 1) * pageSize,
    results?.totalItems ?? 0,
  )
  const hasSearchResults = (results?.results ?? []).length > 0
  const mappedResultsItems: CardProps[] = results?.results.map((item: any) => {
    const mediaUrl = item.url.startsWith('/media')
    const linkUrl =
      // if the item is a media item, we need to prepend the Umbraco base URL
      // we also remove the leading slash from the URL so we have a valid URL
      item.url && mediaUrl ? BuildUmbracoUrl(item.url) : item.url

    return {
      title: item.title,
      tag: item.pageType,
      link: { url: linkUrl, target: mediaUrl ? '_blank' : '_self' },
    }
  })

  const renderHeading = () => {
    if (!searchQuery) {
      return title
    }

    return results?.totalItems === 0
      ? `${t('noResults')} ${searchQuery}`
      : `${t('searchResults')} ${searchQuery}`
  }

  return (
    <CustomGrid minHeight={{ md: '43dvh', lg: '54dvh' }}>
      <GridItem
        colSpan={{ base: 10, md: 10, lg: 10, '2xl': 6 }}
        colStart={{ base: 2, md: 2, lg: 2, '2xl': 4 }}
      >
        {/* full width searchbar with autofocus when you visit the page */}
        <Flex flexDirection='column' gap='2rem'>
          <Heading
            as={'h1'}
            color={'brand.aubergine'}
            w={'full'}
            fontSize={{ base: '2rem', lg: '5.5rem' }}
            fontWeight={400}
            lineHeight={{ base: '120%', md: '140%' }}
            textAlign='center'
          >
            {renderHeading()}
          </Heading>
          <SearchBar
            handleSearch={handleSearch}
            searchInputRef={searchInputRef}
            searchQuery={queryParamStore.query}
            trackEvent={{
              blockName: 'SearchBar',
              searchQuery: searchQuery ?? '',
              totalItems: results?.totalItems ?? 0,
            }}
            placeHolder={t('search')}
          />
          {(results?.totalItems ?? 0) > 0 && (
            <Flex flexDirection='column' gap='1.25rem'>
              {t('results')} {startItem}
              {results?.totalItems !== startItem && ` - ${endItem}`} {t('of')}{' '}
              {results?.totalItems}
            </Flex>
          )}
        </Flex>
      </GridItem>
      <GridItem colSpan={{ base: 10 }} colStart={{ base: 2 }}>
        {isLoading ? (
          <Flex
            display={isLoading ? undefined : 'none'}
            w='full'
            justifyContent={'center'}
          >
            <Spinner color={`${theme}.primary`} />
          </Flex>
        ) : (
          <>
            {hasSearchResults && (
              <Grid templateColumns='repeat(12, 1fr)' gap={{ md: '2.5rem' }}>
                {mappedResultsItems &&
                  mappedResultsItems.map((item, index) => {
                    return (
                      <GridItem
                        key={index}
                        colSpan={{ base: 12, md: 6, xl: 4 }}
                      >
                        <Card
                          link={item.link as ButtonProps}
                          title={item.title}
                          text={item.text}
                          cardType={item.cardType}
                          mediaData={item.mediaData}
                          tag={item.tag}
                          contactLinks={item.contactLinks}
                          date={item.date}
                          percentage={item.percentage}
                          hideOverviewPublicationDate={true}
                        />
                      </GridItem>
                    )
                  })}
              </Grid>
            )}
            {(results?.totalItems ?? 0) > pageSize && (
              <Flex justifyContent='center' paddingTop={16}>
                <Pagination
                  pageCount={results?.totalItems ?? 0}
                  pageSize={pageSize}
                  onPageChange={setSearchPageInfo}
                  page={results?.currentPage}
                />
              </Flex>
            )}
          </>
        )}
      </GridItem>
      {/* Content blocks are only shown when there are no search results */}
      {!isLoading && !hasSearchResults && searchQuery && (
        <GridItem colSpan={12} minWidth={{ lg: 64 }}>
          {/* only show content blocks if there are no results */}
          {contentBlocks}
        </GridItem>
      )}
    </CustomGrid>
  )
}
